export type UploadResourceType = 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'CERTIFICATE';

export interface UploadTransform {
  readonly extension: string;
  readonly mimeType: string;
}

export interface UploadSelectionFormat {
  readonly extensions: readonly string[];
  readonly mimeTypes: readonly string[];
  readonly transform: UploadTransform | null;
}

export interface UploadWireFormat {
  readonly detectedMimeTypes: readonly string[];
  readonly extensions: readonly string[];
  readonly mimeType: string;
}

export interface UploadCapability {
  readonly maxBytes: number;
  readonly resourceType: UploadResourceType;
  readonly selectionFormats: readonly UploadSelectionFormat[];
  readonly wireFormats: readonly UploadWireFormat[];
}

export const UPLOAD_MEBIBYTE_BYTES = 1024 * 1024;
export const DOCUMENT_UPLOAD_MAX_BYTES = 80 * UPLOAD_MEBIBYTE_BYTES;
export const AI_ATTACHMENT_MAX_TOTAL_BYTES = 20 * UPLOAD_MEBIBYTE_BYTES;
export const AI_ATTACHMENT_DOCX_MAX_BYTES = 5 * UPLOAD_MEBIBYTE_BYTES;
export const AI_ATTACHMENT_DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const AI_ATTACHMENT_SPREADSHEET_MAX_BYTES = 5 * UPLOAD_MEBIBYTE_BYTES;
export const AI_ATTACHMENT_SPREADSHEET_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const AI_ATTACHMENT_TEXT_MAX_BYTES = 5 * UPLOAD_MEBIBYTE_BYTES;
export const AI_ATTACHMENT_TEXT_MIME_TYPE = 'text/plain';
export const AI_ATTACHMENT_DOCUMENT_EXTENSIONS = ['pdf', 'txt', 'docx', 'xlsx'] as const;
export const AI_ATTACHMENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export function isAISpreadsheetAttachment(file: { readonly name: string; readonly type?: string }): boolean {
  return file.type?.toLowerCase() === AI_ATTACHMENT_SPREADSHEET_MIME_TYPE || file.name.toLowerCase().endsWith('.xlsx');
}

export function isAIDocxAttachment(file: { readonly name: string; readonly type?: string }): boolean {
  return file.type?.toLowerCase() === AI_ATTACHMENT_DOCX_MIME_TYPE || file.name.toLowerCase().endsWith('.docx');
}

const WEBP_TRANSFORM: UploadTransform = {
  extension: 'webp',
  mimeType: 'image/webp',
};

export const UPLOAD_CAPABILITY_CONTRACT = [
  {
    resourceType: 'IMAGE',
    maxBytes: 10 * UPLOAD_MEBIBYTE_BYTES,
    selectionFormats: [
      { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'], transform: null },
      { extensions: ['png'], mimeTypes: ['image/png'], transform: null },
      { extensions: ['gif'], mimeTypes: ['image/gif'], transform: null },
      { extensions: ['webp'], mimeTypes: ['image/webp'], transform: null },
      { extensions: ['heic'], mimeTypes: ['image/heic'], transform: WEBP_TRANSFORM },
      { extensions: ['heif'], mimeTypes: ['image/heif'], transform: WEBP_TRANSFORM },
    ],
    wireFormats: [
      { extensions: ['jpg', 'jpeg'], mimeType: 'image/jpeg', detectedMimeTypes: ['image/jpeg'] },
      { extensions: ['png'], mimeType: 'image/png', detectedMimeTypes: ['image/png'] },
      { extensions: ['gif'], mimeType: 'image/gif', detectedMimeTypes: ['image/gif'] },
      { extensions: ['webp'], mimeType: 'image/webp', detectedMimeTypes: ['image/webp'] },
    ],
  },
  {
    resourceType: 'VIDEO',
    maxBytes: 100 * UPLOAD_MEBIBYTE_BYTES,
    selectionFormats: [
      { extensions: ['mp4'], mimeTypes: ['video/mp4'], transform: null },
      { extensions: ['mov'], mimeTypes: ['video/quicktime', 'video/mov'], transform: null },
      { extensions: ['avi'], mimeTypes: ['video/x-msvideo', 'video/avi'], transform: null },
      { extensions: ['webm'], mimeTypes: ['video/webm'], transform: null },
    ],
    wireFormats: [
      { extensions: ['mp4'], mimeType: 'video/mp4', detectedMimeTypes: ['video/mp4'] },
      { extensions: ['mov'], mimeType: 'video/quicktime', detectedMimeTypes: ['video/quicktime'] },
      { extensions: ['mov'], mimeType: 'video/mov', detectedMimeTypes: ['video/quicktime'] },
      { extensions: ['avi'], mimeType: 'video/x-msvideo', detectedMimeTypes: ['video/vnd.avi'] },
      { extensions: ['avi'], mimeType: 'video/avi', detectedMimeTypes: ['video/vnd.avi'] },
      { extensions: ['webm'], mimeType: 'video/webm', detectedMimeTypes: ['video/webm'] },
    ],
  },
  {
    resourceType: 'DOCUMENT',
    maxBytes: DOCUMENT_UPLOAD_MAX_BYTES,
    selectionFormats: [
      { extensions: ['pdf'], mimeTypes: ['application/pdf'], transform: null },
      { extensions: ['txt'], mimeTypes: ['text/plain'], transform: null },
      { extensions: ['doc'], mimeTypes: ['application/msword'], transform: null },
      {
        extensions: ['docx'],
        mimeTypes: [AI_ATTACHMENT_DOCX_MIME_TYPE],
        transform: null,
      },
      { extensions: ['xls'], mimeTypes: ['application/vnd.ms-excel'], transform: null },
      {
        extensions: ['xlsx'],
        mimeTypes: [AI_ATTACHMENT_SPREADSHEET_MIME_TYPE],
        transform: null,
      },
      { extensions: ['ppt'], mimeTypes: ['application/vnd.ms-powerpoint'], transform: null },
      {
        extensions: ['pptx'],
        mimeTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
        transform: null,
      },
    ],
    wireFormats: [
      { extensions: ['pdf'], mimeType: 'application/pdf', detectedMimeTypes: ['application/pdf'] },
      { extensions: ['txt'], mimeType: 'text/plain', detectedMimeTypes: ['text/plain'] },
      { extensions: ['doc'], mimeType: 'application/msword', detectedMimeTypes: ['application/x-cfb'] },
      {
        extensions: ['docx'],
        mimeType: AI_ATTACHMENT_DOCX_MIME_TYPE,
        detectedMimeTypes: [AI_ATTACHMENT_DOCX_MIME_TYPE],
      },
      { extensions: ['xls'], mimeType: 'application/vnd.ms-excel', detectedMimeTypes: ['application/x-cfb'] },
      {
        extensions: ['xlsx'],
        mimeType: AI_ATTACHMENT_SPREADSHEET_MIME_TYPE,
        detectedMimeTypes: [AI_ATTACHMENT_SPREADSHEET_MIME_TYPE],
      },
      { extensions: ['ppt'], mimeType: 'application/vnd.ms-powerpoint', detectedMimeTypes: ['application/x-cfb'] },
      {
        extensions: ['pptx'],
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        detectedMimeTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
      },
    ],
  },
  {
    resourceType: 'CERTIFICATE',
    maxBytes: 50 * UPLOAD_MEBIBYTE_BYTES,
    selectionFormats: [{ extensions: ['pdf'], mimeTypes: ['application/pdf'], transform: null }],
    wireFormats: [{ extensions: ['pdf'], mimeType: 'application/pdf', detectedMimeTypes: ['application/pdf'] }],
  },
] as const satisfies readonly UploadCapability[];
